/**
 * GuestList
 * Day-grouped list of guests. Each row expands into a GuestSessionsPanel.
 */

import { motion, useReducedMotion } from 'framer-motion';
import type { PhotoSession } from '@/services/guest.service';
import type { Guest, Questionnaire } from '@/types';
import {
  formatShortDateTime,
  getAvatarHue,
  getEmailInitial,
  toDate,
} from '@/utils/guestFormat';
import { Collapse } from './Collapse';
import { GuestSessionsPanel } from './GuestSessionsPanel';
import '../styles/GuestList.css';

export type GuestGroup = {
  key: string;
  label: string;
  secondaryLabel?: string;
  guests: Guest[];
};

type GuestListProps = {
  groups: GuestGroup[];
  isLoading: boolean;
  expandedGuestId: string | null;
  onToggleGuest: (guestId: string) => void;
  sessionsByGuestId: Record<string, PhotoSession[]>;
  sessionCountByGuestId: Record<string, number>;
  isLoadingSessionCounts: boolean;
  loadingSessionsForGuestId: string | null;
  sessionsErrorByGuestId: Record<string, string | null>;
  onRetrySessions: (guestId: string) => void;
  selectedSessionByGuestId: Record<string, string | null>;
  onSelectSession: (guestId: string, sessionId: string) => void;
  questionnairesById: Record<string, Questionnaire>;
  onOpenPhoto: (url: string) => void;
};

/** Cap the entrance stagger so long lists never feel slow to appear. */
const MAX_STAGGER_INDEX = 12;
const STAGGER_STEP = 0.03;

export function GuestList({
  groups,
  isLoading,
  expandedGuestId,
  onToggleGuest,
  sessionsByGuestId,
  sessionCountByGuestId,
  isLoadingSessionCounts,
  loadingSessionsForGuestId,
  sessionsErrorByGuestId,
  onRetrySessions,
  selectedSessionByGuestId,
  onSelectSession,
  questionnairesById,
  onOpenPhoto,
}: GuestListProps) {
  const reduceMotion = Boolean(useReducedMotion());

  if (isLoading) {
    return (
      <div className="guest-list" aria-busy="true" aria-label="Loading guests">
        <div className="guest-group">
          <span className="sb-skeleton guest-group-skeleton" />
          <span className="sb-skeleton guest-row-skeleton" />
          <span className="sb-skeleton guest-row-skeleton" />
          <span className="sb-skeleton guest-row-skeleton" />
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="guest-list">
        <div className="guest-list-empty">
          <p>No guests found for this organization.</p>
        </div>
      </div>
    );
  }

  let rowIndex = 0;

  return (
    <div className="guest-list">
      {groups.map((group) => (
        <section key={group.key} className="guest-group">
          <header className="guest-group-header">
            <span className="guest-group-title">{group.label}</span>
            {group.secondaryLabel && (
              <span className="guest-group-subtitle">
                {group.secondaryLabel}
              </span>
            )}
            <span className="guest-group-count">
              {group.guests.length}{' '}
              {group.guests.length === 1 ? 'guest' : 'guests'}
            </span>
          </header>

          <div className="guest-group-rows">
            {group.guests.map((guest) => {
              const index = rowIndex++;
              const isExpanded = expandedGuestId === guest.id;
              const sessions = sessionsByGuestId[guest.id];
              const count = sessionCountByGuestId[guest.id] ?? sessions?.length;

              return (
                <GuestRow
                  key={guest.id}
                  guest={guest}
                  index={index}
                  isExpanded={isExpanded}
                  sessionCount={count}
                  isLoadingCount={count === undefined && isLoadingSessionCounts}
                  onToggle={() => onToggleGuest(guest.id)}
                  reduceMotion={reduceMotion}
                >
                  <GuestSessionsPanel
                    guest={guest}
                    sessions={sessions}
                    isLoading={loadingSessionsForGuestId === guest.id}
                    error={sessionsErrorByGuestId[guest.id]}
                    selectedSessionId={selectedSessionByGuestId[guest.id]}
                    onSelectSession={(sessionId) =>
                      onSelectSession(guest.id, sessionId)
                    }
                    onRetry={() => onRetrySessions(guest.id)}
                    questionnairesById={questionnairesById}
                    onOpenPhoto={onOpenPhoto}
                  />
                </GuestRow>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

type GuestRowProps = {
  guest: Guest;
  index: number;
  isExpanded: boolean;
  sessionCount: number | undefined;
  isLoadingCount: boolean;
  onToggle: () => void;
  reduceMotion: boolean;
  children: React.ReactNode;
};

function GuestRow({
  guest,
  index,
  isExpanded,
  sessionCount,
  isLoadingCount,
  onToggle,
  reduceMotion,
  children,
}: GuestRowProps) {
  const lastVisit = toDate(guest.lastSessionAt);
  const panelId = `guest-panel-${guest.id}`;

  return (
    <motion.div
      className={`guest-card ${isExpanded ? 'is-expanded' : ''}`}
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.28,
        ease: 'easeOut',
        delay: reduceMotion
          ? 0
          : Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP,
      }}
    >
      <button
        type="button"
        className="guest-row"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={panelId}
      >
        <span
          className={`guest-avatar hue-${getAvatarHue(guest.email)}`}
          aria-hidden="true"
        >
          {getEmailInitial(guest.email)}
        </span>

        <span className="guest-identity">
          <span className="guest-email" title={guest.email}>
            {guest.email}
          </span>
          <span className="guest-subtext">
            {lastVisit
              ? `Last visit ${formatShortDateTime(lastVisit)}`
              : 'No visits yet'}
          </span>
        </span>

        <SessionCountPill count={sessionCount} isLoading={isLoadingCount} />

        <motion.span
          className="guest-chevron"
          aria-hidden="true"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
          }
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor">
            <path
              d="M4 6l4 4 4-4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.span>
      </button>

      <Collapse open={isExpanded} id={panelId}>
        {children}
      </Collapse>
    </motion.div>
  );
}

function SessionCountPill({
  count,
  isLoading,
}: {
  count: number | undefined;
  isLoading: boolean;
}) {
  if (count === undefined) {
    return isLoading ? (
      <span
        className="guest-pill is-skeleton sb-skeleton"
        aria-label="Loading"
      />
    ) : (
      <span className="guest-pill is-muted">— sessions</span>
    );
  }

  return (
    <span className={`guest-pill ${count === 0 ? 'is-muted' : ''}`}>
      {count} {count === 1 ? 'session' : 'sessions'}
    </span>
  );
}
